import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { getPassengerLabel3 } from '@common/helpers';
import { PassengerType } from '@common/interfaces';
import { ServicingCommonStoreService } from '@common/services';
import { MyBookingStoreService } from '@common/services/my-booking/my-booking-store.service';
import { MyBookingService } from '@common/services/my-booking/my-booking.service';
import { SupportComponent } from '@lib/components/support-class';
import { StaticMsgPipe } from '@lib/pipes';
import { CommonLibService, PageLoadingService } from '@lib/services';
import { Subscription } from 'rxjs';
import {
  GetOrderResponseData,
  GetOrderResponseDataLinkedOrdersInner,
  GetOrderResponseDataTravelersSummaryTravelerNumbers,
} from 'src/sdk-servicing';
import { AppConstants } from '@conf/app.constants';

interface StringIndexedArray {
  [index: string]: number;
}

@Component({
  selector: 'asw-my-booking-infor',
  templateUrl: './my-booking-infor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyBookingInforComponent extends SupportComponent {
  // 1.2 人数
  travelerNumberPeople: string = '';
  // 1.2 予約番号
  orderId: string = '';
  // 1.2 購入・発券状態
  orderStatusLabel: string = '';

  public displayTitle: string = '';
  // 1.2 関連PNR一覧(ヘッダ)
  linkedOrders: Array<GetOrderResponseDataLinkedOrdersInner> = [];
  firstName: string = '';
  lastName: string = '';
  isDisplayReservation: boolean = false;

  public isAwardBooking?: boolean = false;
  public isValid?: boolean = false;
  _subscription = new Subscription();

  /** 定数用 */
  public appConstants = AppConstants;

  constructor(
    private _common: CommonLibService,
    private readonly cdr: ChangeDetectorRef,
    public staticMsg: StaticMsgPipe,
    private readonly _myBookingService: MyBookingService,
    private _servicingCommonStoreService: ServicingCommonStoreService,
    private readonly _loadingService: PageLoadingService,
    private _myBookingStoreService: MyBookingStoreService
  ) {
    super(_common);
  }

  reload() {}
  init() {
    this._subscription.add(
      this._servicingCommonStoreService.getServicingCommonModel$().subscribe((servicingCommon) => {
        const pnr = (servicingCommon.getOrder ?? {}) as GetOrderResponseData;
        this.orderId = pnr.orderId ?? '';
        this.linkedOrders = pnr.linkedOrders ?? [];
        this.isAwardBooking = pnr.orderType?.isAwardBooking ?? false;
        this.isValid = pnr.prices?.isValid ?? false;
        const travelerNumbers = pnr.travelersSummary?.travelerNumbers;
        this.travelerNumberPeople = travelerNumbers ? this.getTravelerNumberPeople(travelerNumbers) : '';

        this.orderStatusLabel = this.getOrderStatusLabel(pnr);
        this.cdr.markForCheck();
      })
    );
    this._subscription.add(
      this._myBookingStoreService.getMyBookingModel$().subscribe((mybooking) => {
        this.isDisplayReservation = mybooking.isDisplayReservation ?? false;
      })
    );
  }

  destroy() {
    this._subscription?.unsubscribe();
  }

  /** (16) 関連PNR一覧(ヘッダ)、関連PNR一覧(モーダル)ボタン押下処理 */
  refreshData({ orderId, travelerName }: GetOrderResponseDataLinkedOrdersInner) {
    this._loadingService.startLoading();
    // 1.1.2.	当該linkerOrder.travelerNameを”/”で区切り、前半を関連PNR搭乗者姓、後半を関連PNR搭乗者名として保持する。
    const names = travelerName?.split('/') ?? [];
    const firstName = names[1] ?? '';
    const lastName = names[0] ?? '';
    this._common.aswServiceStoreService.updateAswService({
      orderId,
      travelDocumentId: '',
      lastName,
      firstName,
    });
    this._myBookingService.runOnInit();
  }

  getOrderStatusLabel(pnr: GetOrderResponseData): string {
    const paymentMethod = pnr.nextActions?.offlinePaymentDetails?.paymentMethod;
    const isAwardBooking = pnr.orderType?.isAwardBooking;
    /**
     * 1 未購入: label.notPurchased
     * 2 支払受付中(コンビニ支払):label.paymentType.uncompleted.cv
     * 3 支払受付中(ネット振込):label.paymentType.uncompleted.ib
     * 4 未発券:label.notTicketed
     * 5 ANABiz発券申請中:label.pendingTicketedApproval
     * 6 発券済み:label.ticketed"
     */
    // 最初に合致したものを表示する。
    switch (pnr.orderStatus) {
      // 1.PNR情報取得API応答.data.orderStatus=”unpurchased”(未購入)の場合、未購入の旨の文言
      case 'unpurchased':
        return 'label.notPurchased';
      case 'paymentAccepted':
        // 2.PNR情報取得API応答.data.orderStatus=”paymentAccepted”(支払受付中)、かつ　PNR情報取得API応答.data.nextActions.offlinePaymentDetails.paymentMethod="convinienceStore"(コンビニ支払)の場合、支払受付中(コンビニ支払)の旨の文言
        if (paymentMethod === 'convinienceStore') {
          return 'label.paymentType.uncompleted.cv';
        }
        return '';
      // 4.PNR情報取得API応答.data.orderStatus=”applyingForTicketing”(発券申請中)の場合、ANA Biz 発券申請中の旨の文言
      case 'applyingForTicketing':
        return 'label.pendingTicketedApproval';
      // 5.PNR情報取得API応答.data.orderStatus="unticketed”(未発券)の場合、未発券の旨の文言
      case 'unticketed':
        // 未発券は有償PNRの場合のみ表示
        if (!isAwardBooking) {
          return 'label.notTicketed';
        }
        return '';
      // 6.PNR情報取得API応答.data.orderStatus=”ticketed”(発券済み)の場合、発券済みの旨の文言
      case 'ticketed':
        return 'label.ticketed';
      default:
        return '';
    }
  }

  /**
   * 1.2
   * 大人
   * ヤングアダルト
   * 小児
   * 席あり幼児
   * 席なし幼児
   * @param travelers
   * @returns
   */
  getTravelerNumberPeople(travelers: GetOrderResponseDataTravelersSummaryTravelerNumbers) {
    const listTraveler: Array<string> = [];
    // 1.2.3 予約詳細store.予約情報を表示するフラグ=trueが存在するかつ、以下のいずれかに該当する場合、表示する。
    // 1.PNR情報取得API応答.data.orderType.isAwardBooking=false(有償PNR)
    // 2.PNR情報取得API応答.data.orderType.isAwardBooking=true(特典PNR)かつ、PNR情報取得API応答.prices.isValid=true(支払詳細が有効)
    if (this.isDisplayReservation && (!this.isAwardBooking || (this.isAwardBooking && this.isValid))) {
      const arrTravelerKey: Array<PassengerType> = ['ADT', 'B15', 'CHD', 'INS', 'INF'];
      arrTravelerKey.forEach((key) => {
        const value = (travelers as StringIndexedArray)?.[key] || 0;
        if (value > 0) listTraveler.push(this.staticMsg.transform(getPassengerLabel3(key), { '0': value }));
      });
    }
    return listTraveler.join(this.staticMsg.transform('label.commaSeparater') + ' ');
  }
}
